import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  NgZone,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../../../core/services/api.service';
import { API_ROUTES } from '../../../../core/constants/api-routes';
import {
  Creator,
  FamilyMember,
  FamilySpace,
  StoryCategory,
  StoryMasterData,
  StoryType,
} from '../../../../core/models/story.models';
import {
  GetStorybookStoriesRequest,
  GetStorybookStoriesResponse,
  StorybookStory,
} from '../../../../core/models/storybook.models';

const PAGE_SIZE = 4;
const VIEW_MODE_KEY = 'fm_create_storybook_view_mode';

@Component({
  selector: 'app-create-storybooks',
  templateUrl: './create-storybooks.html',
  styleUrl: './create-storybooks.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule],
})
export class CreateStorybooks implements OnInit, OnDestroy {
  // ── Dependency injection ───────────────────────────────────────────────────
  private readonly api = inject(ApiService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly zone = inject(NgZone);

  // ── UI state ───────────────────────────────────────────────────────────────
  readonly isInitializing = signal(true);
  readonly hasError = signal(false);
  readonly isFilterPanelOpen = signal(false);
  readonly viewMode = signal<'grid' | 'list'>(this.readViewMode());

  // ── Server data ────────────────────────────────────────────────────────────
  // Assuming storybookId is provided somehow, defaulting to 0 for now
  storybookId = 0;

  readonly stories = signal<StorybookStory[]>([]);
  readonly totalRecords = signal(0);
  readonly selectedStoriesCount = signal(0);
  readonly hasMoreStories = signal(false);
  readonly isLoadingMore = signal(false);
  readonly hasStoriesInSystem = signal(false);

  readonly categories = signal<StoryCategory[]>([]);
  readonly familySpaces = signal<FamilySpace[]>([]);
  readonly familyMembers = signal<FamilyMember[]>([]);
  readonly creators = signal<Creator[]>([]);

  // ── Active filter state ────────────────────────────────────────────────────
  customSearch = '';
  readonly submittedSearch = signal('');
  readonly activeCategoryId = signal<number | null>(null);
  readonly activeViewType = signal<string | null>(null);
  readonly activeFamilySpaceId = signal<string | null>(null);
  readonly activeTaggedPersonIds = signal<string[]>([]);
  readonly activeCreatorIds = signal<string[]>([]);
  readonly pageNumber = signal(1);

  readonly viewTypeOptions: { label: string; value: string }[] = [
    { label: 'All', value: '' },
    { label: 'My Stories', value: '1' },
    { label: 'Family Stories', value: '2' },
    { label: 'Shared Stories', value: '3' },
  ];

  // ── Derived filter lists ───────────────────────────────────────────────────
  readonly filteredFamilyMembers = computed(() => {
    const familyId = this.activeFamilySpaceId();
    if (!familyId) return this.familyMembers();
    return this.familyMembers().filter((m) => m.familySpaceId === familyId);
  });

  readonly filteredCreators = computed(() => {
    const familyId = this.activeFamilySpaceId();
    if (!familyId) return this.creators();

    const byFamilySpace = this.creators().filter((c) => c.familySpaceId === familyId);
    if (byFamilySpace.length > 0) return byFamilySpace;

    const familyUserIds = new Set(
      this.familyMembers()
        .filter((m) => m.familySpaceId === familyId && !!m.userId)
        .map((m) => m.userId as string),
    );
    if (familyUserIds.size > 0) {
      const byUserId = this.creators().filter((c) => familyUserIds.has(c.userId));
      if (byUserId.length > 0) return byUserId;
    }

    return this.creators();
  });

  // ── Private ────────────────────────────────────────────────────────────────
  private isRequestInFlight = false;
  private scrollObserver?: IntersectionObserver;

  // ────────────────────────────────────────────────────────────────────────────
  // Lifecycle
  // ────────────────────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.initPage();
  }

  ngOnDestroy(): void {
    this.scrollObserver?.disconnect();
  }

  private async initPage(): Promise<void> {
    await this.loadMasterData();
    await this.loadStories();
  }

  private async loadMasterData(): Promise<void> {
    try {
      const resp = await this.api.request<StoryMasterData>(
        'GET',
        API_ROUTES.GET_STORY_MASTER_DATA,
        undefined,
        { showToaster: false, showLoader: false },
      );
      if (resp.status && resp.data) {
        const d = resp.data;
        this.categories.set(d.categories ?? d.activeCategories ?? []);
        this.familySpaces.set(d.familySpaces ?? d.activeFamilyList ?? d.activeFamilies ?? []);
        this.familyMembers.set(d.familyMembers ?? d.familyMemberList ?? []);
        this.creators.set(d.creators ?? d.creatorList ?? d.allCreatorList ?? []);
      }
    } catch (err) {
      console.error('[CreateStorybooks] Error loading master data:', err);
    }
  }

  async loadStories(isLoadMore = false): Promise<void> {
    if (this.isRequestInFlight) return;

    this.isRequestInFlight = true;
    if (isLoadMore) {
      this.isLoadingMore.set(true);
    } else {
      this.hasError.set(false);
    }

    try {
      const params = new URLSearchParams();
      params.append('PageNumber', this.pageNumber().toString());
      params.append('PageSize', PAGE_SIZE.toString());

      if (this.storybookId > 0) {
        params.append('StorybookId', this.storybookId.toString());
      }

      const search = this.submittedSearch();
      if (search) params.append('SearchText', search);

      const viewType = this.activeViewType();
      if (viewType !== null) params.append('ViewType', viewType);

      const categoryId = this.activeCategoryId();
      if (categoryId !== null) params.append('CategoryId', categoryId.toString());

      const familyId = this.activeFamilySpaceId();
      if (familyId !== null) params.append('FamilySpaceId', familyId);

      const creatorIds = this.activeCreatorIds();
      if (creatorIds.length > 0) {
        creatorIds.forEach((id) => params.append('CreatorIds', id));
      }

      const taggedIds = this.activeTaggedPersonIds();
      if (taggedIds.length > 0) {
        taggedIds.forEach((id) => params.append('TaggedPersonIds', id));
      }

      const endpoint = `${API_ROUTES.STORY_BOOK_GET_STORIES}?${params.toString()}`;

      const resp = await this.api.request<GetStorybookStoriesResponse>('GET', endpoint, undefined, {
        showToaster: false,
        showLoader: false,
        useToken: true,
      });

      if (resp.status && resp.data) {
        const incoming = resp.data.stories ?? [];
        const total = resp.data.totalStories ?? 0;
        
        this.selectedStoriesCount.set(resp.data.selectedStoriesCount ?? 0);

        if (isLoadMore) {
          this.stories.update((existing) => [...existing, ...incoming]);
        } else {
          this.stories.set(incoming);
        }

        this.totalRecords.set(total);
        if (total > 0) this.hasStoriesInSystem.set(true);
        this.hasMoreStories.set(this.stories().length < total);
      } else {
        if (!isLoadMore) {
          this.stories.set([]);
          this.totalRecords.set(0);
          this.selectedStoriesCount.set(0);
          this.hasError.set(true);
        }
        this.hasMoreStories.set(false);
      }
    } catch (err) {
      if (!isLoadMore) {
        this.hasError.set(true);
        this.stories.set([]);
        this.totalRecords.set(0);
        this.selectedStoriesCount.set(0);
      }
      this.hasMoreStories.set(false);
    } finally {
      this.isLoadingMore.set(false);
      this.isRequestInFlight = false;
      this.isInitializing.set(false);

      if (!isLoadMore && isPlatformBrowser(this.platformId)) {
        setTimeout(() => this.setupScrollObserver(), 100);
      }
    }
  }

  private setupScrollObserver(): void {
    this.scrollObserver?.disconnect();
    const sentinel = document.querySelector('#storiesScrollSentinel');
    if (!sentinel) return;

    this.scrollObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !this.isRequestInFlight && this.hasMoreStories()) {
          this.zone.run(() => {
            this.pageNumber.update((n) => n + 1);
            this.loadStories(true);
          });
        }
      },
      { threshold: 0.1 },
    );
    this.scrollObserver.observe(sentinel);
  }

  // ────────────────────────────────────────────────────────────────────────────
  // User interaction handlers
  // ────────────────────────────────────────────────────────────────────────────

  toggleFilterPanel(): void {
    this.isFilterPanelOpen.update((open) => !open);
  }

  async onSearch(): Promise<void> {
    const term = this.customSearch?.trim() ?? '';
    this.customSearch = term;
    this.submittedSearch.set(term);
    this.pageNumber.set(1);
    await this.loadStories();
  }

  async clearSearch(): Promise<void> {
    this.customSearch = '';
    this.submittedSearch.set('');
    this.pageNumber.set(1);
    await this.loadStories();
  }

  setCategory(categoryId: number | null): void {
    this.activeCategoryId.set(categoryId);
    this.pageNumber.set(1);
    this.loadStories();
  }

  onViewTypeChange(value: string): void {
    this.activeViewType.set(value === '' ? null : value);
    this.pageNumber.set(1);
    this.loadStories();
  }

  onFamilySpaceChange(value: string): void {
    const familyId = value === '' ? null : value;
    this.activeFamilySpaceId.set(familyId);

    if (familyId) {
      const validMemberIds = new Set(
        this.familyMembers()
          .filter((m) => m.familySpaceId === familyId)
          .map((m) => m.familyMemberId),
      );
      this.activeTaggedPersonIds.update((ids) => ids.filter((id) => validMemberIds.has(id)));

      const familyUserIds = new Set(
        this.familyMembers()
          .filter((m) => m.familySpaceId === familyId && !!m.userId)
          .map((m) => m.userId as string),
      );
      if (familyUserIds.size > 0) {
        this.activeCreatorIds.update((ids) => ids.filter((id) => familyUserIds.has(id)));
      }
    }

    this.pageNumber.set(1);
    this.loadStories();
  }

  toggleTaggedPerson(id: string): void {
    this.activeTaggedPersonIds.update((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );
    this.pageNumber.set(1);
    this.loadStories();
  }

  toggleCreator(id: string): void {
    this.activeCreatorIds.update((ids) =>
      ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id],
    );
    this.pageNumber.set(1);
    this.loadStories();
  }

  clearAllFilters(): void {
    this.customSearch = '';
    this.submittedSearch.set('');
    this.activeCategoryId.set(null);
    this.activeViewType.set(null);
    this.activeFamilySpaceId.set(null);
    this.activeTaggedPersonIds.set([]);
    this.activeCreatorIds.set([]);
    this.pageNumber.set(1);
    this.loadStories();
  }

  setViewMode(mode: 'grid' | 'list'): void {
    this.viewMode.set(mode);
    this.writeViewMode(mode);
  }

  toggleStorySelection(story: StorybookStory): void {
    story.isSelected = !story.isSelected;
    // Update local count intuitively
    if (story.isSelected) {
      this.selectedStoriesCount.update((c) => c + 1);
    } else {
      this.selectedStoriesCount.update((c) => Math.max(0, c - 1));
    }
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Template utility helpers
  // ────────────────────────────────────────────────────────────────────────────

  get hasAnyActiveFilter(): boolean {
    return (
      !!this.submittedSearch() ||
      this.activeCategoryId() !== null ||
      this.activeViewType() !== null ||
      this.activeFamilySpaceId() !== null ||
      this.activeTaggedPersonIds().length > 0 ||
      this.activeCreatorIds().length > 0
    );
  }

  get hasActiveFilters(): boolean {
    const creatorSet = new Set(this.creators().map((c) => c.userId));
    const hasValidCreator = this.activeCreatorIds().some((id) => creatorSet.has(id));
    return (
      this.activeCategoryId() !== null ||
      this.activeViewType() !== null ||
      this.activeFamilySpaceId() !== null ||
      this.activeTaggedPersonIds().length > 0 ||
      hasValidCreator
    );
  }

  getActiveFamilyName(): string {
    const id = this.activeFamilySpaceId();
    if (!id) return '';
    return this.familySpaces().find((f) => f.familySpaceId === id)?.familyName ?? '';
  }

  getActiveCategoryName(): string {
    const id = this.activeCategoryId();
    if (id === null) return '';
    return this.categories().find((c) => c.categoryId === id)?.categoryName ?? '';
  }

  getViewTypeName(): string {
    const vt = this.activeViewType();
    const option = this.viewTypeOptions.find((o) => o.value === vt);
    return option ? option.label : '';
  }

  getAuthorInitials(name: string | undefined): string {
    if (!name?.trim()) return '?';
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((w) => w[0].toUpperCase())
      .join('');
  }

  private readViewMode(): 'grid' | 'list' {
    if (isPlatformBrowser(this.platformId)) {
      const saved = localStorage.getItem(VIEW_MODE_KEY);
      if (saved === 'grid' || saved === 'list') return saved;
    }
    return 'grid';
  }

  private writeViewMode(mode: 'grid' | 'list'): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    }
  }

  // -- Date picker -----------------------------------------------------------
  readonly memoryDate = signal<string>('');
  readonly showDatePicker = signal(false);
  readonly pickerDropUp = signal(false);
  readonly pickerViewYear = signal(new Date().getFullYear());
  readonly pickerViewMonth = signal(new Date().getMonth());

  private static readonly MONTH_NAMES = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  readonly pickerMonthLabel = computed(
    () => `${CreateStorybooks.MONTH_NAMES[this.pickerViewMonth()]} ${this.pickerViewYear()}`,
  );

  readonly memoryDateDisplay = computed(() => {
    const val = this.memoryDate();
    if (!val) return '';
    const parts = val.split('-');
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (!y || !m || !d) return val;
    return `${CreateStorybooks.MONTH_NAMES[m - 1]} ${d}, ${y}`;
  });

  readonly pickerCalendarDays = computed(() => {
    const year = this.pickerViewYear();
    const month = this.pickerViewMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selVal = this.memoryDate();
    const selected = selVal ? new Date(selVal + 'T00:00:00') : null;

    type DayCell = {
      date: Date | null;
      day: number | null;
      isCurrentMonth: boolean;
      isToday: boolean;
      isSelected: boolean;
      isDisabled: boolean;
    };
    const cells: DayCell[] = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push({
        date: null,
        day: null,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        isDisabled: true,
      });
    }
    for (let d = 1; d <= totalDays; d++) {
      const date = new Date(year, month, d);
      date.setHours(0, 0, 0, 0);
      cells.push({
        date,
        day: d,
        isCurrentMonth: true,
        isToday: date.getTime() === today.getTime(),
        isSelected: selected ? date.getTime() === selected.getTime() : false,
        isDisabled: false, // allow past/future dates for memories
      });
    }
    while (cells.length % 7 !== 0) {
      cells.push({
        date: null,
        day: null,
        isCurrentMonth: false,
        isToday: false,
        isSelected: false,
        isDisabled: true,
      });
    }
    return cells;
  });

  openDatePicker(event: MouseEvent): void {
    const val = this.memoryDate();
    if (val) {
      const [y, m] = val.split('-').map(Number);
      this.pickerViewYear.set(y);
      this.pickerViewMonth.set(m - 1);
    }
    const trigger = event.currentTarget as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    this.pickerDropUp.set(spaceBelow < 320 && rect.top > 320);
    this.showDatePicker.set(true);
  }

  closeDatePicker(): void {
    this.showDatePicker.set(false);
  }

  selectPickerDate(date: Date | null): void {
    if (!date) return;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const value = `${y}-${m}-${d}`;
    this.memoryDate.set(value);
    this.showDatePicker.set(false);
  }

  pickerPrevMonth(): void {
    if (this.pickerViewMonth() === 0) {
      this.pickerViewMonth.set(11);
      this.pickerViewYear.update((y) => y - 1);
    } else {
      this.pickerViewMonth.update((v) => v - 1);
    }
  }

  pickerNextMonth(): void {
    if (this.pickerViewMonth() === 11) {
      this.pickerViewMonth.set(0);
      this.pickerViewYear.update((y) => y + 1);
    } else {
      this.pickerViewMonth.update((v) => v + 1);
    }
  }
}
